import {useMutation} from '@apollo/react-hooks';

import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    AddAttachmentToLead as AddAttachmentToLeadMutation,
    AddAttachmentToLeadVariables,
    LeadFileAddInput,
    FileFragment,
} from 'nutshell-graphql-types';

import {getFileType} from '../../../../../core-entity/widgets/attachments-widget/helpers';

import ADD_ATTACHMENT_TO_LEAD from '../mutations/add-attachment-to-lead.graphql';

export const useAddAttachmentToLead = (): {
    addAttachmentToLead: (
        arg1: LeadFileAddInput,
        arg2?: User | null | undefined,
        arg3?: FileFragment[] | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [addAttachmentToLead, {loading, error}] = useMutation<
        AddAttachmentToLeadMutation,
        AddAttachmentToLeadVariables
    >(ADD_ATTACHMENT_TO_LEAD);

    return {
        addAttachmentToLead: (
            input: LeadFileAddInput,
            user?: User | null,
            existingFiles?: FileFragment[] | null
        ) => {
            let mutationReturn = null;

            if (user && existingFiles) {
                mutationReturn = addAttachmentToLead({
                    variables: {input},
                    // @ts-expect-error
                    optimisticResponse: () => {
                        const newFiles = input.files.map((file) => ({
                            __typename: 'EntityAttributeValueFile',
                            fileId: String(Date.now()) + String(Math.random()),
                            href: file.locationUri,
                            viewHref: file.locationUri,
                            viewableInBrowser: false,
                            fileType: getFileType(file.mimeType, file.filename),
                            uploadedTime: Math.floor(Date.now() / 1000),
                            name: file.filename,
                            creatorUser: {
                                __typename: 'User',
                                id: user.id,
                                name: user.name,
                                avatarUrl: user.avatarUrl,
                                initials: user.initials,
                            },
                        }));

                        return {
                            leadFilesAdd: {
                                __typename: 'LeadFileAddPayload',
                                lead: {
                                    __typename: 'Lead',
                                    id: input.lead,
                                    files: [...newFiles.reverse(), ...existingFiles],
                                },
                            },
                        };
                    },
                });
            } else {
                mutationReturn = addAttachmentToLead({
                    variables: {input},
                });
            }

            return mutationReturn;
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
